import Pagina, { IPaginaInfo, PaginaInfo } from "@/Models/Utils/Pagina/Pagina";
import { useMovimentacaoConsilidadoPessoaListar } from "@/Services/Domain/Relatorio/MovimentacaoConsolidadoPessoaServices";
import { Descriptions, Form, FormProps, List, ListProps, Table, Typography, Row, Col, Button } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { Movimentacao } from "@/Models/Financeiro/Conta/Movimentacao";
import Rateio from "@/Models/Financeiro/Titulo/Rateio";
import Utilitario from "@/Utils/Utilitario";
import PessoaSelectComTipo from "@/Components/Populados/Pessoa/PessoaSelect";
import CentroCustoSelect from "../../../CentroConta/CentroCustoSelect";
import PlanoDeContasSelect from "../../../PlanoDeContas/PlanoDeContasSelect";
import MovimentacaoConsolidadoPessoa from "@/Models/Domain/FInanceiro/Relatorio/MovimentacaoConsolidadaPessoa";
import { EnumRegistroTipo } from "@/Models/Financeiro/PlanoContasTipoRegistro";
import InputRangePicker from "@/Components/Base/Input/InputRangePicker";

export interface IMovimentacaoConsolidadaConsultaScreenFormType {
    pagina?:IPaginaInfo;
    codPessoa?:number;
    intervalo?:[Dayjs|undefined, Dayjs|undefined];
    codigosPessoas?:Array<number>;
    codigosCentroCusto?:Array<number>;
    codigosPlanoContas?:Array<number>;
}

export interface IMovimentacaoConsolidadaConsultaScreenProps extends FormProps<IMovimentacaoConsolidadaConsultaScreenFormType> {
    listProps?:Omit<ListProps<MovimentacaoConsolidadoPessoa>, "dataSource">;
}

export default function MovimentacaoConsolidadaConsultaScreen({
    listProps,
    form : formParam,
    initialValues,
    component,
    layout,
    ...rest
}:IMovimentacaoConsolidadaConsultaScreenProps) {
    const dataAtual = new Date();
    const [ consultar, setConsultar ] = useState(false);
    const [newForm] = Form.useForm<IMovimentacaoConsolidadaConsultaScreenFormType>();
    const form = formParam ?? newForm;
    const inputRequest = Form.useWatch([], form);
    const paginaInicial = new Pagina();
    paginaInicial.tamanho = 5;

    const {
        data,
        isLoading
    } = useMovimentacaoConsilidadoPessoaListar({
        ...inputRequest,
        pagina : inputRequest?.pagina ?? paginaInicial,
        enabled : consultar,
        onFinish() {
            setConsultar(false);
        },
    });

    return <Form
        {...rest}
        form={form}
        component={component === undefined ? "div" : component}
        layout={layout === undefined ? "vertical" : layout}
        initialValues={{
            ...initialValues,
            pagina : initialValues?.pagina === undefined ? new PaginaInfo() : initialValues?.pagina,
            intervalo : [ dayjs(new Date(dataAtual.getFullYear(), 0, 1)), dayjs(new Date()) ],
        }}
        onFinish={() => {
            form.setFieldsValue({
                pagina : {
                    pagina : 1
                }
            } as any);

            setConsultar(true);
        }}
    >
        <Row gutter={8} wrap>
            <Form.Item hidden name={["pagina"]}></Form.Item>
            <Col xs={24} sm={10} md={8} lg={8} xl={6} xxl={4}>
                <Form.Item<IMovimentacaoConsolidadaConsultaScreenFormType> label="Intervalo" name={"intervalo"} rules={[ { 
                    required : true
                } ]}>
                    <InputRangePicker />
                </Form.Item>
            </Col>
            <Col xs={24} sm={14} md={16} lg={16} xl={18} xxl={20}>
                <PessoaSelectComTipo<IMovimentacaoConsolidadaConsultaScreenFormType>
                    names={{
                        tipo: ["codPessoaTipo"],
                        "*": ["codPessoa"],
                    }}
                    rules={{
                        tipo: [
                            {
                                required: true,
                            },
                        ],
                        "*": [
                            {
                                required: true,
                                message: "Por favor, selecione um devedor",
                            },
                        ],
                    }}
                />
            </Col>
            <Col xs={24}>
                <PessoaSelectComTipo<IMovimentacaoConsolidadaConsultaScreenFormType>
                    mode="multiple"
                    names={{
                        tipo: ["codigosPessoasTipo"],
                        "*": ["codigosPessoas"],
                    }}
                />
            </Col>
            <Col xs={24}>
                <Form.Item label="Centros de custos" name={["codigosCentroCusto"]}>
                    <CentroCustoSelect 
                        mode="multiple"
                    />
                </Form.Item>
            </Col>
            <Col xs={24}>
                <Form.Item label="Planos de contas" name={["codigosPlanoContas"]}>
                    <PlanoDeContasSelect 
                        mode="multiple"
                        filtros={{
                            tipoRegistro : {
                                codigo : EnumRegistroTipo.Item
                            }
                        }}
                    />
                </Form.Item>
            </Col>
        </Row>
        <Row gutter={8} wrap>
            <Col xs={"auto"}>
                <Button type="primary" loading={isLoading} onClick={() => form.submit()}>
                    Listar
                </Button>
            </Col>
        </Row> 
        <List 
            {...listProps}
            rowKey={(m) => m.pessoa.codigo ?? 0}
            pagination={{
                pageSize : data?.tamanho,
                total : data?.totalRegistros,
                current : data?.pagina,
                onChange(page, pageSize) {
                    form.setFieldsValue({
                        pagina : {
                            pagina : page ?? 1,
                            tamanho : pageSize ?? 15
                        }
                    } as any);

                    setConsultar(true);
                },
            }}
            loading={isLoading}
            itemLayout="vertical"
            dataSource={data?.registros}
            renderItem={(item) => <List.Item>
                <List.Item.Meta
                    title={<Descriptions
                        bordered
                        size={"small"}
                        column={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4, xxl: 4 }}
                        items={[
                            {
                                key: '1',
                                label: 'Nome',
                                children: item.pessoa.nome,
                                span : { xs : 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1 }
                            },
                            {
                                key: '2',
                                label: 'Nome social',
                                children: item.pessoa.nomeSocial ?? "Não informado",
                                span : { xs : 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1 }
                            },
                            {
                                key: '3',
                                label: 'Total Bruto',
                                children: <Typography.Text type={item.valorTotalLiquido >= 0 ? "success" : "danger"}>{Utilitario.toStringDinheiro(item.valorTotalBruto)}</Typography.Text>,
                                span : { xs : 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1 }
                            },
                            {
                                key: '4',
                                label: 'Total Liq.',
                                children: <Typography.Text type={item.valorTotalLiquido >= 0 ? "success" : "danger"}>{Utilitario.toStringDinheiro(item.valorTotalLiquido)}</Typography.Text>,
                                span : { xs : 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1 }
                            }
                        ]}
                    />}
                />
                <Table<Movimentacao>
                    dataSource={item.movimentacoes} 
                    pagination={false}
                    size={"small"}
                    columns={[
                        {
                            key : "documento",
                            title : "Doc.",
                            dataIndex : ["codTitulo"]
                        },
                        {
                            key : "identificadorBanco",
                            title : "ID. Banco",
                            dataIndex : ["identificadorBanco"],
                            render: (value: string, record: Movimentacao) => value ?? " - "
                        },
                        {
                            key : "planoConta",
                            title : "P.C.",
                            dataIndex : ["titulo", "rateios"],
                            render: (value: Rateio[], record: Movimentacao) => value.find(f => true)?.planoConta?.descricao
                        },
                        {
                            key : "centroCusto",
                            title : "C.C.",
                            dataIndex : ["titulo", "rateios"],
                            render: (value: Rateio[], record: Movimentacao) => value.find(f => true)?.centroCusto?.descricao
                        },
                        {
                            key : "conta",
                            title : "Conta",
                            dataIndex : ["conta", "nome"]
                        },
                        {
                            key : "vencimento",
                            title : "Dt. Venc.",
                            dataIndex : ["titulo", "dataVencimento"],
                            render: (value: Date, record: Movimentacao) => value.toLocaleString().replace(',', '').substring(0, 16)
                        },
                        {
                            key : "dataMovimentacao",
                            title : "Dt. Mov.",
                            dataIndex : ["dataMovimentacao"],
                            render: (value: Date, record: Movimentacao) => value.toLocaleString().replace(',', '').substring(0, 16)
                        },
                        {
                            key : "valor",
                            title : "Valor",
                            dataIndex : ["valorTransacao"],
                            render: (value: number, record: Movimentacao) => <Typography.Text type={record.entrada ? "success" : "danger"}>{Utilitario.toStringDinheiro((value + (record.tituloTarifa?.valorRecebido ?? 0)) * (record.entrada ? 1 : -1))}</Typography.Text>
                        },
                        {
                            key : "valorTransacao",
                            title : "Vl. Recebido",
                            dataIndex : ["valorTransacao"],
                            render: (value: number, record: Movimentacao) => <Typography.Text type={record.entrada ? "success" : "danger"}>{Utilitario.toStringDinheiro(value * (record.entrada ? 1 : -1))}</Typography.Text>
                        }
                    ]}
                />
            </List.Item>}
        />
    </Form> 
}